/**
 * Initialize the map overlay functionality
 * @param {HTMLElement} block - The block element
 * @param {SVGElement} svgElement - The SVG element to be cloned and displayed in the overlay
 */
function initializeMapOverlay( block, svgElement ) {

	// Find elements
	const statesContainer = block.closest( '.states' );
	if ( !statesContainer ) {

		return;

	}

	const viewMapButton = statesContainer.querySelector( '.states__list-toggle' );
	const mapOverlay = document.querySelector( '.map-overlay' );

	if ( !viewMapButton || !mapOverlay ) {

		return;

	}

	const mapOverlayClose = mapOverlay.querySelector( '.map-overlay__close' );
	const mapWrapper = mapOverlay.querySelector( '.map-overlay__map-wrapper' );
	const zoomInButton = mapOverlay.querySelector( '.map-overlay__zoom-in' );
	const zoomOutButton = mapOverlay.querySelector( '.map-overlay__zoom-out' );

	// State for tracking zoom level and pan position
	const mapState = {
		scale: 1,
		translateX: 0,
		translateY: 0,
		lastX: 0,
		lastY: 0,
		isDragging: false
	};

	// Open map overlay
	viewMapButton.addEventListener( 'click', () => {

		// Clone the SVG and add it to the overlay
		if ( !mapWrapper.querySelector( 'svg' ) ) {

			const svgClone = svgElement.cloneNode( true );
			mapWrapper.appendChild( svgClone );

			// Reset map state
			mapState.scale = 1;
			mapState.translateX = 0;
			mapState.translateY = 0;

			// Apply initial transform
			updateMapTransform();

			// Add event listeners to the cloned SVG
			setupMapInteractions( svgClone );

		}

		// Show the overlay
		mapOverlay.classList.add( 'active' );

		// Prevent body scrolling when overlay is open
		document.body.style.overflow = 'hidden';

	} );

	// Close map overlay
	mapOverlayClose.addEventListener( 'click', () => {

		mapOverlay.classList.remove( 'active' );
		document.body.style.overflow = '';

	} );

	// Close overlay when clicking outside the container
	mapOverlay.addEventListener( 'click', ( e ) => {

		if ( e.target === mapOverlay ) {

			mapOverlay.classList.remove( 'active' );
			document.body.style.overflow = '';

		}

	} );

	// Zoom controls
	zoomInButton.addEventListener( 'click', () => {

		mapState.scale = Math.min( mapState.scale * 1.2, 5 ); // Max zoom level: 5x
		updateMapTransform();

	} );

	zoomOutButton.addEventListener( 'click', () => {

		mapState.scale = Math.max( mapState.scale / 1.2, 0.5 ); // Min zoom level: 0.5x
		updateMapTransform();

	} );


	/**
	 * Setup map interactions (pan and zoom)
	 * @param {SVGElement} svgElement - The SVG element to be cloned and displayed in the overlay
	 */
	function setupMapInteractions( svgElement ) {

		// Mouse wheel zoom
		mapWrapper.addEventListener( 'wheel', ( e ) => {

			e.preventDefault();

			// Get mouse position relative to the map
			const rect = mapWrapper.getBoundingClientRect();
			const mouseX = e.clientX - rect.left;
			const mouseY = e.clientY - rect.top;

			// Calculate zoom direction
			const scaleFactor = e.deltaY < 0 ? 1.1 : 0.9;

			// Adjust scale
			const newScale = Math.max( 0.5, Math.min( 5, mapState.scale * scaleFactor ) );

			// Calculate new position to zoom toward mouse cursor
			const scaleRatio = newScale / mapState.scale;
			mapState.translateX = mouseX - ( mouseX - mapState.translateX ) * scaleRatio;
			mapState.translateY = mouseY - ( mouseY - mapState.translateY ) * scaleRatio;

			// Update scale
			mapState.scale = newScale;

			// Apply transform
			updateMapTransform();

		} );

		// Pan with mouse drag
		mapWrapper.addEventListener( 'mousedown', ( e ) => {

			e.preventDefault();
			mapState.isDragging = true;
			mapState.lastX = e.clientX;
			mapState.lastY = e.clientY;
			mapWrapper.style.cursor = 'grabbing';

		} );

		document.addEventListener( 'mousemove', ( e ) => {

			if ( !mapState.isDragging ) {

				return;

			}

			// Calculate the distance moved
			const deltaX = e.clientX - mapState.lastX;
			const deltaY = e.clientY - mapState.lastY;

			// Update the last position
			mapState.lastX = e.clientX;
			mapState.lastY = e.clientY;

			// Update the translation
			mapState.translateX += deltaX;
			mapState.translateY += deltaY;

			// Apply transform
			updateMapTransform();

		} );

		document.addEventListener( 'mouseup', () => {

			mapState.isDragging = false;
			mapWrapper.style.cursor = 'grab';

		} );

		// Touch support for mobile devices
		let lastTouchDistance = 0;
		let touchStartX = 0;
		let touchStartY = 0;
		let touchMoved = false;
		let touchStartTime = 0;
		const TOUCH_THRESHOLD = 10; // Pixels of movement to consider a pan instead of a tap

		// Touch start
		mapWrapper.addEventListener( 'touchstart', ( e ) => {

			e.preventDefault();
			touchStartTime = Date.now();
			touchMoved = false;

			if ( e.touches.length === 1 ) {

				// Single touch for panning
				mapState.isDragging = true;
				mapState.lastX = e.touches[0].clientX;
				mapState.lastY = e.touches[0].clientY;
				touchStartX = e.touches[0].clientX;
				touchStartY = e.touches[0].clientY;

			} else if ( e.touches.length === 2 ) {

				// Two fingers for pinch zoom
				mapState.isDragging = false;

				// Calculate the initial distance between the two touch points
				const touch1 = e.touches[0];
				const touch2 = e.touches[1];
				lastTouchDistance = Math.hypot(
					touch2.clientX - touch1.clientX,
					touch2.clientY - touch1.clientY
				);

			}

		} );

		// Touch move
		mapWrapper.addEventListener( 'touchmove', ( e ) => {

			e.preventDefault();

			if ( e.touches.length === 1 && mapState.isDragging ) {

				// Single touch panning
				const touch = e.touches[0];

				// Calculate the distance moved
				const deltaX = touch.clientX - mapState.lastX;
				const deltaY = touch.clientY - mapState.lastY;

				// Calculate total movement from start position to determine if this is a pan or tap
				const totalMoveX = Math.abs( touch.clientX - touchStartX );
				const totalMoveY = Math.abs( touch.clientY - touchStartY );

				if ( totalMoveX > TOUCH_THRESHOLD || totalMoveY > TOUCH_THRESHOLD ) {

					touchMoved = true;

				}

				// Update the last position
				mapState.lastX = touch.clientX;
				mapState.lastY = touch.clientY;

				// Update the translation
				mapState.translateX += deltaX;
				mapState.translateY += deltaY;

				// Apply transform
				updateMapTransform();

			} else if ( e.touches.length === 2 ) {

				touchMoved = true;
				// Pinch zoom
				const touch1 = e.touches[0];
				const touch2 = e.touches[1];

				// Calculate the center point between the two touch points
				const centerX = ( touch1.clientX + touch2.clientX ) / 2;
				const centerY = ( touch1.clientY + touch2.clientY ) / 2;

				// Calculate the new distance between the two touch points
				const newTouchDistance = Math.hypot(
					touch2.clientX - touch1.clientX,
					touch2.clientY - touch1.clientY
				);

				// Calculate the scale factor
				if ( lastTouchDistance > 0 ) {

					const rect = mapWrapper.getBoundingClientRect();
					const mapX = centerX - rect.left;
					const mapY = centerY - rect.top;

					const scaleFactor = newTouchDistance / lastTouchDistance;
					const newScale = Math.max( 0.5, Math.min( 5, mapState.scale * scaleFactor ) );

					// Calculate new position to zoom toward the center of the pinch
					const scaleRatio = newScale / mapState.scale;
					mapState.translateX = mapX - ( mapX - mapState.translateX ) * scaleRatio;
					mapState.translateY = mapY - ( mapY - mapState.translateY ) * scaleRatio;

					// Update scale
					mapState.scale = newScale;

					// Apply transform
					updateMapTransform();

				}

				lastTouchDistance = newTouchDistance;

			}

		} );

		// Touch end event for the entire map wrapper
		mapWrapper.addEventListener( 'touchend', ( e ) => {

			const touchDuration = Date.now() - touchStartTime;

			// Short touch (< 300ms) without much movement is considered a tap
			const isQuickTap = touchDuration < 300 && !touchMoved;

			// If it was a quick tap, check if we tapped on a state path
			if ( isQuickTap && e.changedTouches.length === 1 ) {

				const touch = e.changedTouches[0];

				// Get elements at the touch position
				const elementsUnderTouch = document.elementsFromPoint( touch.clientX, touch.clientY );

				// Find if any of them is a state path
				for ( const element of elementsUnderTouch ) {

					if (
						( element.tagName === 'path' || element.tagName === 'rect' ) &&
						element.hasAttribute( 'data-title' ) &&
						( !element.classList.contains( 'state-shortname' ) || element.classList.contains( 'shortname-hit-area' ) )
					) {

						// This is a state path or hit area, update the selected state
						updateSelectedStateInfo( element );
						break;

					}

				}

			}

			// Reset flags
			mapState.isDragging = false;
			touchMoved = false;

		} );


		const statePaths = svgElement.querySelectorAll( 'path[data-title]:not(.state-shortname)' );
		const selectedStateElement = mapOverlay.querySelector( '.map-overlay__selected-state' );

		// Also get hit areas in the overlay
		const hitAreas = svgElement.querySelectorAll( '.shortname-hit-area' );

		/**
		 * Helper function to check if the current viewport is mobile
		 * @return {boolean} - True if the viewport is mobile, false otherwise
		 */
		const isMobile = () => window.innerWidth < 1024;


		/**
		 * Update the selected state information
		 * @param {SVGElement} element - The SVG element to update
		 */
		const updateSelectedStateInfo = ( element ) => {


			if ( !isMobile() ) {

				return;

			}

			const stateName = element.getAttribute( 'data-title' );
			const stateUrl = element.getAttribute( 'data-url' );
			let stateClass = element.className.baseVal;

			// If this is a hit area, try to get the class from the corresponding state path
			if ( element.classList.contains( 'shortname-hit-area' ) ) {

				const stateId = element.getAttribute( 'data-for-id' );
				if ( stateId ) {

					const statePath = svgElement.querySelector( `path#${stateId}:not(.state-shortname)` );
					if ( statePath ) {

						stateClass = statePath.className.baseVal;

					}

				}

			}

			let colorClass = '';
			if ( stateClass ) {

				const colorClassMatch = stateClass.match( /color-[a-z-]+/ );
				if ( colorClassMatch ) {

					colorClass = colorClassMatch[0];

				}

			}


			if ( selectedStateElement ) {


				const stateNameElement = selectedStateElement.querySelector( '.selected-state__state' );
				if ( stateNameElement ) {

					stateNameElement.innerHTML = `<span class="${colorClass}"></span>${stateName}`;

				}


				const linkElement = selectedStateElement.querySelector( '.selected-state__link a' );
				if ( linkElement && stateUrl ) {

					linkElement.href = stateUrl;

				}


				selectedStateElement.style.display = 'flex';

			}

		};

		// Add click events for all state paths
		statePaths.forEach( path => {


			/**
			 * Handle state selection
			 * @param {Event} e - The event object
			 */
			const handleStateSelect = function handleStateSelect( e ) {

				if ( !isMobile() ) {

					return;

				}

				e.preventDefault();
				updateSelectedStateInfo( this );

				// Don't trigger default behavior if this was intentional selection
				e.stopPropagation();

			};

			// Add event listeners for both mouse and touch events
			path.addEventListener( 'click', handleStateSelect );
			path.addEventListener( 'touchend', ( e ) => {

				// Prevent regular click from firing
				e.preventDefault();

				// Only proceed if this was a tap (not the end of a scroll/pan)
				if ( !mapState.isDragging ) {

					updateSelectedStateInfo( path );

				}

				// Reset dragging state for future interactions
				mapState.isDragging = false;

			} );

		} );

		// Also add click events for hit areas
		hitAreas.forEach( hitArea => {

			/**
			 * Handle hit area selection
			 * @param {Event} e - The event object
			 */
			const handleHitAreaSelect = function handleHitAreaSelect( e ) {

				if ( !isMobile() ) {

					return;

				}

				e.preventDefault();
				updateSelectedStateInfo( this );

				// Don't trigger default behavior if this was intentional selection
				e.stopPropagation();

			};

			// Add event listeners for both mouse and touch events
			hitArea.addEventListener( 'click', handleHitAreaSelect );
			hitArea.addEventListener( 'touchend', ( e ) => {

				// Prevent regular click from firing
				e.preventDefault();

				// Only proceed if this was a tap (not the end of a scroll/pan)
				if ( !mapState.isDragging ) {

					updateSelectedStateInfo( hitArea );

				}

				// Reset dragging state for future interactions
				mapState.isDragging = false;

			} );

		} );


		const stateShortnames = svgElement.querySelectorAll( '.state-shortname[data-title]' );
		stateShortnames.forEach( shortname => {

			/**
			 * Handle shortname selection
			 * @param {Event} e - The event object
			 */
			const handleShortnameSelect = function handleShortnameSelect( e ) {

				if ( !isMobile() ) {

					return;

				}

				e.preventDefault();


				const stateName = this.getAttribute( 'data-title' );
				const stateUrl = this.getAttribute( 'data-url' );

				const stateId = this.getAttribute( 'id' );
				const matchingPath = stateId ? svgElement.querySelector( `path#${stateId}:not(.state-shortname)` ) : null;

				let colorClass = '';
				if ( matchingPath ) {

					const stateClass = matchingPath.className.baseVal;
					if ( stateClass ) {

						const colorClassMatch = stateClass.match( /color-[a-z-]+/ );
						if ( colorClassMatch ) {

							colorClass = colorClassMatch[0];

						}

					}

				}


				if ( selectedStateElement ) {


					const stateNameElement = selectedStateElement.querySelector( '.selected-state__state' );
					if ( stateNameElement ) {

						stateNameElement.innerHTML = `<span class="${colorClass}"></span>${stateName}`;

					}


					const linkElement = selectedStateElement.querySelector( '.selected-state__link a' );
					if ( linkElement && stateUrl ) {

						linkElement.href = stateUrl;

					}

					selectedStateElement.style.display = 'block';

				}

				e.stopPropagation();

			};

			shortname.addEventListener( 'click', handleShortnameSelect );
			shortname.addEventListener( 'touchend', ( e ) => {

				if ( !isMobile() ) {

					return;

				}

				e.preventDefault();

				if ( !touchMoved && !mapState.isDragging ) {

					handleShortnameSelect.call( shortname, e );

				}

			} );

		} );


		const shortNameHitAreas = svgElement.querySelectorAll( '.shortname-hit-area' );
		shortNameHitAreas.forEach( hitArea => {

			/**
			 * Handle hit area selection
			 * @param {Event} e - The event object
			 */
			const handleHitAreaSelect = function handleHitAreaSelect( e ) {

				if ( !isMobile() ) {

					return;

				}

				e.preventDefault();

				const stateName = this.getAttribute( 'data-title' );
				const stateUrl = this.getAttribute( 'data-url' );

				const stateId = this.getAttribute( 'data-for-id' );
				const matchingPath = stateId ? svgElement.querySelector( `path#${stateId}:not(.state-shortname)` ) : null;

				let colorClass = '';
				if ( matchingPath ) {

					const stateClass = matchingPath.className.baseVal;
					if ( stateClass ) {

						const colorClassMatch = stateClass.match( /color-[a-z-]+/ );
						if ( colorClassMatch ) {

							colorClass = colorClassMatch[0];

						}

					}

				}

				if ( selectedStateElement ) {

					const stateNameElement = selectedStateElement.querySelector( '.selected-state__state' );
					if ( stateNameElement ) {

						stateNameElement.innerHTML = `<span class="${colorClass}"></span>${stateName}`;

					}

					const linkElement = selectedStateElement.querySelector( '.selected-state__link a' );
					if ( linkElement && stateUrl ) {

						linkElement.href = stateUrl;

					}

					// Make sure the element is visible
					selectedStateElement.style.display = 'block';

				}

				// Don't trigger default click behavior if this was intentional selection
				e.stopPropagation();

			};

			// Add event listeners for both mouse and touch events
			hitArea.addEventListener( 'click', handleHitAreaSelect );
			hitArea.addEventListener( 'touchend', ( e ) => {

				if ( !isMobile() ) {

					return;

				}

				// Prevent regular click from firing
				e.preventDefault();

				// Only proceed if this was a tap (not a pan/drag)
				if ( !touchMoved && !mapState.isDragging ) {

					handleHitAreaSelect.call( hitArea, e );

				}

			} );

		} );

		// Set initial cursor style
		mapWrapper.style.cursor = 'grab';

		// Make states and hit areas clickable in the overlay
		const states = svgElement.querySelectorAll( '.state, .shortname-hit-area' );
		states.forEach( state => {

			state.style.cursor = 'pointer';
			state.addEventListener( 'click', ( e ) => {

				// Get state code - either directly or from data-for-id attribute
				let stateCode = state.getAttribute( 'id' );
				if ( !stateCode && state.classList.contains( 'shortname-hit-area' ) ) {

					stateCode = state.getAttribute( 'data-for-id' );

				}

				if ( stateCode ) {

					// Find the corresponding state item in the list
					const stateItem = statesContainer.querySelector( `.state-item[data-state="${stateCode}"]` );
					if ( stateItem ) {

						// Close the overlay
						mapOverlay.classList.remove( 'active' );
						document.body.style.overflow = '';

						// Scroll to the state item and highlight it
						stateItem.scrollIntoView( { behavior: 'smooth', block: 'center' } );

						// Flash highlight effect
						stateItem.classList.add( 'highlight' );
						setTimeout( () => {

							stateItem.classList.remove( 'highlight' );

						}, 1500 );

					}

				}

			} );

		} );

	}


	/**
	 * Update the map transform based on current state
	 *wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwww
	 * @return {void}
	 */
	function updateMapTransform() {

		const mapSvg = mapWrapper.querySelector( 'svg' );
		if ( !mapSvg ) {

			return;

		}

		// Use requestAnimationFrame for smoother animations
		requestAnimationFrame( () => {

			// Apply transform to the SVG with hardware acceleration
			mapSvg.style.transform = `translate3d(${mapState.translateX}px, ${mapState.translateY}px, 0) scale(${mapState.scale})`;
			mapSvg.style.transformOrigin = '0 0';

		} );

	}

	// Add debounced resize handler
	let resizeTimeout;
	window.addEventListener( 'resize', () => {

		clearTimeout( resizeTimeout );
		resizeTimeout = setTimeout( () => {

			// Recalculate map bounds and update transform
			const mapContainer = document.querySelector( '.b-interactive-map__map' );
			if ( mapContainer ) {

				const mapBounds = mapContainer.getBoundingClientRect();
				// Ensure map stays within bounds
				mapState.translateX = Math.min( Math.max( mapState.translateX, -mapBounds.width * ( mapState.scale - 1 ) ), 0 );
				mapState.translateY = Math.min( Math.max( mapState.translateY, -mapBounds.height * ( mapState.scale - 1 ) ), 0 );
				updateMapTransform();

			}

		}, 150 ); // 150ms debounce

	} );

}


/**
 * Initialize the map overlay functionality
 * @param {HTMLElement} block - The block element
 * @param {SVGElement} svgElement - The SVG element to be cloned and displayed in the overlay
 */
document.addEventListener( 'DOMContentLoaded', () => {


	/**
	 * Initialize the map overlay functionality
	 * @param {HTMLElement} block - The block element
	 * @param {SVGElement} svgElement - The SVG element to be cloned and displayed in the overlay
	 */
	document.querySelectorAll( '.b-interactive-map' ).forEach( ( block ) => {

		/////////////////////
		// BLOCK CODE HERE //
		/////////////////////

		console.log( 'init' );

		// Create tooltip element
		const mapContainer = block.querySelector( '.b-interactive-map__map' );
		if ( !mapContainer ) {

			return;

		}

		// Get the actual SVG element
		const svgElement = mapContainer.querySelector( 'svg' );
		if ( !svgElement ) {

			return;

		}

		// Create tooltip element
		const tooltip = document.createElement( 'div' );
		tooltip.className = 'b-interactive-map__tooltip';
		mapContainer.appendChild( tooltip );  // Append to mapContainer instead of block for better positioning

		// Get all path elements with data-title attribute (these are the state shapes)
		const statePaths = svgElement.querySelectorAll( 'path[data-title]:not(.state-shortname)' );

		// Get all shortname elements
		const stateShortnames = svgElement.querySelectorAll( '.state-shortname[data-title]' );

		// Track current active state to prevent flickering
		let currentActiveState = null;
		let isTooltipVisible = false;
		let tooltipTimer = null;

		// Create hit areas for shortnames
		createHitAreasForShortnames();

		/**
		 * Show tooltip for a state
		 * @param {SVGElement} statePath - The state path element
		 * @return {void}
		 */
		function showTooltipForState( statePath ) {

			if ( currentActiveState === statePath ) {

				return;

			}

			// Clear any pending hide timer
			if ( tooltipTimer ) {

				clearTimeout( tooltipTimer );
				tooltipTimer = null;

			}

			// Remove highlight from previous active state
			if ( currentActiveState && !currentActiveState.classList.contains( 'state-shortname' ) &&
				!currentActiveState.classList.contains( 'shortname-hit-area' ) &&
				!currentActiveState.classList.contains( 'region-hit-area' ) ) {

				currentActiveState.classList.remove( 'active-state' );

			}

			// Set new active state
			currentActiveState = statePath;

			// Skip if no title
			const title = statePath.getAttribute( 'data-title' );
			if ( !title ) {

				return;

			}

			// Highlight current state (only if it's a regular state path, not a shortname or hit area)
			if ( !statePath.classList.contains( 'state-shortname' ) &&
				!statePath.classList.contains( 'shortname-hit-area' ) &&
				!statePath.classList.contains( 'region-hit-area' ) ) {

				statePath.classList.add( 'active-state' );

			}

			// Set tooltip content
			tooltip.textContent = title;

			// Get the bounding box of the element
			const elementBounds = statePath.getBoundingClientRect();
			const mapBounds = mapContainer.getBoundingClientRect();

			// Calculate center position
			const elementWidth = elementBounds.width;
			const elementCenterX = elementBounds.left + ( elementWidth / 2 );

			// Check if this is a shortname element or hit area
			const isShortname = statePath.classList.contains( 'state-shortname' ) ||
				statePath.classList.contains( 'shortname-hit-area' ) ||
				statePath.classList.contains( 'region-hit-area' );

			// Calculate vertical position - different logic for shortnames
			let tooltipTop;

			if ( isShortname ) {

				// For shortnames, position above the element with more space
				const elementHeight = elementBounds.height;
				const elementTop = elementBounds.top;

				// Position the tooltip above the shortname with a bigger gap
				tooltipTop = elementTop - mapBounds.top - elementHeight - 35;

			} else {

				// For regular state shapes, use the top position with standard offset
				const elementTop = elementBounds.top;
				tooltipTop = elementTop - mapBounds.top - 15;

			}

			// Position tooltip
			tooltip.style.display = 'block';

			// Calculate position relative to the map container
			const tooltipLeft = elementCenterX - mapBounds.left;

			// Apply position
			tooltip.style.transform = 'translateX(-50%)'; // Center horizontally
			tooltip.style.left = `${tooltipLeft}px`;
			tooltip.style.top = `${tooltipTop}px`;

			isTooltipVisible = true;

		}

		/**
		 * Hide tooltip with a delay
		 * @return {void}
		 */
		function hideTooltipWithDelay() {

			// Don't immediately hide - set a small delay to allow moving between elements
			if ( tooltipTimer ) {

				clearTimeout( tooltipTimer );

			}

			tooltipTimer = setTimeout( () => {

				// Only hide if we're not over any state or shortname
				if ( currentActiveState && !currentActiveState.classList.contains( 'state-shortname' ) &&
					!currentActiveState.classList.contains( 'shortname-hit-area' ) &&
					!currentActiveState.classList.contains( 'region-hit-area' ) ) {

					currentActiveState.classList.remove( 'active-state' );

				}

				currentActiveState = null;

				tooltip.style.display = 'none';
				isTooltipVisible = false;
				tooltipTimer = null;

			}, 100 ); // 100ms delay before hiding

		}

		/**
		 * Handle mouse enter on state paths
		 * @param {Event} e - The event object
		 */
		function handleStateMouseEnter( e ) {

			const element = e.currentTarget || this;
			showTooltipForState( element );

			// Prevent event from bubbling to avoid conflicts
			e.stopPropagation();

		}

		/**
		 * Handle mouse leave on state paths
		 * @param {Event} e - The event object
		 */
		function handleStateMouseLeave( e ) {

			hideTooltipWithDelay();

			// Prevent event from bubbling to avoid conflicts
			e.stopPropagation();

		}

		/**
		 * Handle state click
		 * @param {Event} e - The event object
		 */
		function handleStateClick( e ) {

			const element = e.currentTarget || this;
			const url = element.getAttribute( 'data-url' );
			if ( url && url.length > 0 ) {

				window.location.href = url;

			}

		}

		/**
		 * Create invisible hit area rectangles for state shortnames
		 * @return {void}
		 */
		function createHitAreasForShortnames() {

			// First create individual hit areas for each shortname
			stateShortnames.forEach( shortname => {

				// Get bounding box of the shortname
				const bbox = shortname.getBBox();

				// Create rectangle that covers the entire shortname with larger padding for better touch targets
				const hitArea = document.createElementNS( "http://www.w3.org/2000/svg", "rect" );

				// Use smaller padding (3-5px) as requested
				const padding = 5; // Reduced from 20px to 5px
				hitArea.setAttribute( "x", bbox.x - padding );
				hitArea.setAttribute( "y", bbox.y - padding );
				hitArea.setAttribute( "width", bbox.width + ( padding * 2 ) );
				hitArea.setAttribute( "height", bbox.height + ( padding * 2 ) );
				hitArea.setAttribute( "class", "shortname-hit-area" );
				hitArea.setAttribute( "data-title", shortname.getAttribute( "data-title" ) );

				// Store the ID reference to the corresponding state
				if ( shortname.getAttribute( "id" ) ) {

					hitArea.setAttribute( "data-for-id", shortname.getAttribute( "id" ) );

				}

				// Add URL data if available from the shortname
				if ( shortname.hasAttribute( "data-url" ) ) {

					hitArea.setAttribute( "data-url", shortname.getAttribute( "data-url" ) );

				} else {

					// If shortname doesn't have URL, try to get it from the corresponding state path
					const stateId = shortname.getAttribute( "id" );
					if ( stateId ) {

						const correspondingState = svgElement.querySelector( `path#${stateId}:not(.state-shortname)` );
						if ( correspondingState && correspondingState.hasAttribute( "data-url" ) ) {

							hitArea.setAttribute( "data-url", correspondingState.getAttribute( "data-url" ) );

						}

					}

				}

				// Critical: Ensure the hit area is positioned BEFORE the shortname in the DOM
				// so it doesn't block events and is properly layered
				const parent = shortname.parentNode;
				parent.insertBefore( hitArea, parent.firstChild ); // Insert at the beginning of the parent

				// Add event listeners to hit area
				hitArea.addEventListener( 'mouseenter', function handleHitAreaMouseEnter( e ) {

					showTooltipForState( this );
					e.stopPropagation();

				} );
				hitArea.addEventListener( 'mouseleave', function handleHitAreaMouseLeave( e ) {

					hideTooltipWithDelay();
					e.stopPropagation();

				} );
				hitArea.addEventListener( 'click', function handleHitAreaTouchEnd( e ) {

					e.preventDefault();
					const url = this.getAttribute( 'data-url' );
					if ( url && url.length > 0 ) {

						window.location.href = url;

					}

				} );

				// Add touch events for mobile
				hitArea.addEventListener( 'touchstart', function handleHitAreaTouchStart( e ) {

					// Prevent default to avoid both touch and mouse events firing
					e.preventDefault();
					// Show tooltip on touch
					showTooltipForState( this );

				} );

				hitArea.addEventListener( 'touchend', function handleHitAreaTouchEnd( e ) {

					// Prevent default to avoid both touch and mouse events firing
					e.preventDefault();
					// Handle click action
					const url = this.getAttribute( 'data-url' );
					if ( url && url.length > 0 ) {

						window.location.href = url;

					}

				} );

			} );

			// Create additional hit areas for clusters of states (like New England)
			createRegionalHitAreas();

		}

		/**
		 * Create additional hit areas for clusters of neighboring states
		 * Helps with the gaps between state abbreviations
		 * @return {void}
		 */
		function createRegionalHitAreas() {

			// Define regions that need extra coverage (clusters of neighboring states)
			const regionClusters = [
				// New England cluster (NH, VT, MA, etc)
				{
					ids: ['NH', 'VT', 'MA', 'ME', 'CT', 'RI'],
					name: 'New England'
				},
				// Mid-Atlantic cluster (NY, NJ, PA, etc)
				{
					ids: ['NY', 'NJ', 'PA', 'DE', 'MD'],
					name: 'Mid-Atlantic'
				},
				// Add additional clusters as needed
			];

			regionClusters.forEach( cluster => {

				// Gather all shortnames for this cluster
				const clusterShortnames = [];

				cluster.ids.forEach( stateId => {

					const shortname = svgElement.querySelector( `.state-shortname#${stateId}` );
					if ( shortname ) {

						clusterShortnames.push( shortname );

					}

				} );

				if ( clusterShortnames.length > 1 ) {

					// Calculate the bounding box that encompasses all states in the cluster
					let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;

					clusterShortnames.forEach( shortname => {

						const bbox = shortname.getBBox();
						minX = Math.min( minX, bbox.x );
						minY = Math.min( minY, bbox.y );
						maxX = Math.max( maxX, bbox.x + bbox.width );
						maxY = Math.max( maxY, bbox.y + bbox.height );

					} );

					// Add small padding for gaps between states
					const padding = 5; // Reduced from 25px to 5px

					// Create a region hit area
					const regionHitArea = document.createElementNS( "http://www.w3.org/2000/svg", "rect" );
					regionHitArea.setAttribute( "x", minX - padding );
					regionHitArea.setAttribute( "y", minY - padding );
					regionHitArea.setAttribute( "width", ( maxX - minX ) + ( padding * 2 ) );
					regionHitArea.setAttribute( "height", ( maxY - minY ) + ( padding * 2 ) );
					regionHitArea.setAttribute( "class", "region-hit-area" );
					// For debugging - uncomment to verify placement
					// regionHitArea.setAttribute("fill", "rgba(255,0,0,0.2)");

					// Handle mouse interactions for the region
					regionHitArea.addEventListener( 'mousemove', ( e ) => {

						// Find the closest state to the mouse position
						const mouseX = e.clientX;
						const mouseY = e.clientY;

						let closestState = null;
						let minDistance = Infinity;

						clusterShortnames.forEach( shortname => {

							const bbox = shortname.getBoundingClientRect();
							const centerX = bbox.left + ( bbox.width / 2 );
							const centerY = bbox.top + ( bbox.height / 2 );

							const distance = Math.sqrt(
								Math.pow( mouseX - centerX, 2 ) +
								Math.pow( mouseY - centerY, 2 )
							);

							if ( distance < minDistance ) {

								minDistance = distance;
								closestState = shortname;

							}

						} );

						if ( closestState ) {

							showTooltipForState( closestState );

						}

					} );

					regionHitArea.addEventListener( 'mouseleave', handleStateMouseLeave );

					// Critical: Insert the region hit area at the very beginning of SVG
					// This ensures it's below all other elements in stacking order
					const svgRoot = clusterShortnames[0].closest( 'svg' );
					if ( svgRoot ) {

						svgRoot.insertBefore( regionHitArea, svgRoot.firstChild );

					}

				}

			} );

		}

		// Create hit areas for shortnames
		createHitAreasForShortnames();

		/**
		 * Find the matching state path for a shortname
		 * @param {SVGElement} shortname - The shortname element
		 * @return {SVGElement|null} - The matching state path or null if no match
		 */
		function findMatchingStatePath( shortname ) {

			const id = shortname.getAttribute( 'id' );
			if ( !id ) {

				return null;

			}

			// Find path with matching ID
			return svgElement.querySelector( `path#${id}:not(.state-shortname)` );

		}

		/**
		 * Check if mouse is over any state element
		 * @param {Event} e - The event object
		 */
		function checkForStateUnderMouse( e ) {

			// If we're directly over a state element with data-title, we can ignore
			if ( e.target.hasAttribute( 'data-title' ) ) {

				return;

			}

			// Get element under current mouse position
			const elementsUnderMouse = document.elementsFromPoint( e.clientX, e.clientY );

			// Check if any of those elements is a state path with data-title
			for ( const element of elementsUnderMouse ) {

				if ( element.tagName === 'path' && element.hasAttribute( 'data-title' ) ) {

					// Found a state path under the mouse
					showTooltipForState( element );
					return;

				}

			}

			// If we get here, no state path was found
			hideTooltipWithDelay();

		}

		// Add event listeners to all state paths
		statePaths.forEach( path => {

			// Add hover listeners
			path.addEventListener( 'mouseenter', handleStateMouseEnter );
			path.addEventListener( 'mouseleave', handleStateMouseLeave );

			// Add click listener for redirection
			path.addEventListener( 'click', handleStateClick );

			// Update styles to indicate clickability
			path.style.cursor = 'pointer';

		} );

		// Add event listeners to all state shortnames
		stateShortnames.forEach( shortname => {

			// Add hover listeners
			shortname.addEventListener( 'mouseenter', handleStateMouseEnter );
			shortname.addEventListener( 'mouseleave', handleStateMouseLeave );

			// Add click listener - try to get URL from matching state path
			shortname.addEventListener( 'click', ( e ) => {

				// Try to find matching state path to get URL
				const matchingStatePath = findMatchingStatePath( shortname );
				const url = matchingStatePath ? matchingStatePath.getAttribute( 'data-url' ) :
					shortname.getAttribute( 'data-url' );

				if ( url && url.length > 0 ) {

					window.location.href = url;

				}

			} );

			// Show pointer cursor for shortnames too
			shortname.style.cursor = 'pointer';

		} );

		// Add general mousemove listener to the entire SVG to handle gaps and text elements
		svgElement.addEventListener( 'mousemove', checkForStateUnderMouse );

		// Handle map overlay functionality
		initializeMapOverlay( block, svgElement );

		// Add event listeners for sorting and pagination
		const statesContainer = block.closest( '.states' );
		if ( !statesContainer ) {

			return;

		}

		const stateTable = statesContainer.querySelector( '.states-table' );
		console.log( 'stateTable:', stateTable );

		if ( stateTable ) {

			// Track current sorting state
			let currentSortState = {
				orderby: 'title', // Default sort by title
				order: 'ASC'      // Default ascending order
			};

			// Initialize sort buttons with proper classes based on current sort state
			const sortButtons = stateTable.querySelectorAll( '.js-state-sort, .js-status-sort' );
			console.log( 'sortButtons:', sortButtons );

			// Add 'asc' class to the default sort button (title column)
			const defaultSortButton = stateTable.querySelector( '.js-state-sort' );
			if ( defaultSortButton ) {

				defaultSortButton.classList.add( 'asc' );
				defaultSortButton.setAttribute( 'aria-sort', 'ascending' );

			}

			sortButtons.forEach( ( button ) => {

				button.addEventListener( 'click', function handleSortClick() {

					console.log( 'clicked' );

					// Determine which column was clicked
					const clickedOrderby = this.classList.contains( 'js-state-sort' ) ? 'title' : 'status';

					// If clicking the same column, toggle order; otherwise set new column with ASC order
					let newOrder;
					if ( clickedOrderby === currentSortState.orderby ) {

						newOrder = currentSortState.order === 'ASC' ? 'DESC' : 'ASC';

					} else {

						newOrder = 'ASC';

					}

					// Update UI for all sort buttons
					sortButtons.forEach( btn => {

						// Remove all sort-related classes and attributes
						btn.classList.remove( 'asc', 'desc', 'active' );
						btn.removeAttribute( 'aria-sort' );

					} );

					// Add appropriate classes and attributes to the clicked button
					this.classList.add( newOrder.toLowerCase(), 'active' );
					this.setAttribute( 'aria-sort', `${newOrder.toLowerCase()}ending` );

					// Fetch sorted results with current sort state info
					fetchSortedStateLaws( newOrder, clickedOrderby, 1, currentSortState.orderby, currentSortState.order );

					// Update current sort state
					currentSortState.orderby = clickedOrderby;
					currentSortState.order = newOrder;

				} );

			} );

			/**
			 * Fetch sorted state laws via AJAX
			 * @param {string} order - The new sort order (ASC or DESC)
			 * @param {string} orderby - The field to sort by
			 * @param {number} paged - The page number
			 * @param {string} currentOrderby - The current orderby value
			 * @param {string} currentOrder - The current order value
			 */
			function fetchSortedStateLaws( order, orderby, paged, currentOrderby = '', currentOrder = '' ) {

				// Check if themeVars exists
				if ( typeof themeVars === 'undefined' ) {

					console.error( 'Error: themeVars is not defined. AJAX cannot proceed.' );
					return;

				}

				const data = new FormData();
				data.append( 'action', 'sort_state_laws' );
				data.append( 'nonce', themeVars.security );
				data.append( 'order', order );
				data.append( 'orderby', orderby );
				data.append( 'paged', paged );
				data.append( 'current_orderby', currentOrderby );
				data.append( 'current_order', currentOrder );

				fetch( themeVars.ajax_url, {
					method: 'POST',
					body: data,
				} )
					.then( response => {

						// Check if response is ok (status in the range 200-299)
						if ( !response.ok ) {

							throw new Error( `HTTP error! Status: ${response.status}` );

						}

						// First check the content type
						const contentType = response.headers.get( 'content-type' );
						if ( contentType && contentType.includes( 'text/html' ) ) {

							// If we got HTML instead of JSON, read the text and throw it as an error
							return response.text().then( text => {

								throw new Error( `Server returned HTML instead of JSON. First 100 chars: ${text.slice( 0, 100 )}` );

							} );

						}

						return response.json();

					} )
					.then( response => {

						console.log( 'AJAX response:', response );

						if ( response.success ) {

							stateTable.querySelector( '.states-table__body' ).innerHTML = response.data.html;

							// Check if pagination container exists
							const paginationContainer = stateTable.querySelector( '.states-table__pagination' );
							console.log( 'Pagination container:', paginationContainer );
							console.log( 'Pagination HTML:', response.data.paginationHtml );

							if ( paginationContainer ) {

								// If pagination container exists, replace it
								paginationContainer.outerHTML = response.data.paginationHtml;

							} else {

								// If no pagination container, check where to insert it

								// Try to find the table body to append after it
								const tableBody = stateTable.querySelector( '.states-table__body' );
								if ( tableBody ) {

									console.log( 'Appending pagination after table body' );
									// Create a temporary container to parse the HTML
									const temp = document.createElement( 'tbody' );
									temp.innerHTML = response.data.paginationHtml;

									// Insert the pagination row right after the table body
									tableBody.insertAdjacentHTML( 'afterend', response.data.paginationHtml );

								} else {

									// If no table body found, look for the table
									console.error( 'Could not find table body, attempting to add to table' );
									// Try to append to the table directly
									stateTable.insertAdjacentHTML( 'beforeend', response.data.paginationHtml );

								}

							}

							// Add event listeners to new pagination links
							const paginationLinks = stateTable.querySelectorAll( '.page-numbers' );
							paginationLinks.forEach( function handlePaginationLink( link ) {

								link.addEventListener( 'click', function handlePaginationClick( e ) {

									e.preventDefault();
									const page = parseInt( this.dataset.page, 10 ) || 1;
									fetchSortedStateLaws( order, orderby, page, currentSortState.orderby, currentSortState.order );

								} );

							} );

							// Update current sort state
							currentSortState.orderby = orderby;
							currentSortState.order = order;

						}

					} )
					.catch( ( error ) => console.error( 'Error fetching sorted state laws:', error ) );

			}

			// Initialize pagination links
			const initialPaginationLinks = stateTable.querySelectorAll( '.page-numbers' );
			initialPaginationLinks.forEach( function handleInitialPaginationLink( link ) {

				link.addEventListener( 'click', function handleInitialPaginationClick( e ) {

					e.preventDefault();
					const page = parseInt( this.dataset.page, 10 ) || 1;
					fetchSortedStateLaws( currentSortState.order, currentSortState.orderby, page );

				} );

			} );

		}

	} );

} );
